import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList
} from '@/components/ui/combobox';
import { Spinner } from '@/components/ui/spinner';
import { useSimpleSelect } from './model';

export interface SelectOption {
  value: string;
  label: string;
  /** Extra strings the option also matches when filtering (e.g. organization abbreviation). */
  keywords?: string[];
}

interface SimpleSelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  onInputValueChange?: (value: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

export function SimpleSelect({
  options,
  value,
  onChange,
  onInputValueChange,
  placeholder = 'Select an option',
  emptyMessage = 'No items found.',
  className = 'w-60',
  isLoading = false,
  disabled = false
}: SimpleSelectProps) {
  const { items, selectedLabel, handleValueChange, getKeywords } = useSimpleSelect({
    options,
    value,
    onChange
  });

  const comboboxFilter = ComboboxPrimitive.useFilter();

  // Same collator matching as the Combobox default, extended to option keywords
  // (e.g. organization abbreviation) so server results matched by them survive.
  const filter = useMemo(
    () => (label: string, query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || trimmedQuery === selectedLabel) return true;
      if (comboboxFilter.contains(label, trimmedQuery)) return true;
      return getKeywords(label)?.some((keyword) => comboboxFilter.contains(keyword, trimmedQuery)) ?? false;
    },
    [comboboxFilter, selectedLabel, getKeywords]
  );

  const isSelectingRef = useRef(false);
  const [inputValue, setInputValue] = useState(selectedLabel || '');

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);

      if (!isSelectingRef.current && onInputValueChange) {
        onInputValueChange(newValue);
      }
    },
    [onInputValueChange]
  );

  const handleSelection = useCallback(
    (label: string | null) => {
      isSelectingRef.current = true;
      handleValueChange(label);
      setInputValue(label || '');

      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    },
    [handleValueChange]
  );

  useEffect(() => {
    if (!isSelectingRef.current) {
      setInputValue(selectedLabel || '');
    }
  }, [selectedLabel]);

  return (
    <Combobox
      items={items}
      value={inputValue}
      onValueChange={handleSelection}
      onInputValueChange={handleInputChange}
      filter={filter}
      disabled={disabled}>
      <ComboboxInput
        placeholder={placeholder}
        className={className}
        showClear={!!inputValue && !disabled}
        showTrigger={!disabled}
        disabled={disabled}
      />
      <ComboboxContent>
        {isLoading ? (
          <ComboboxEmpty className="flex items-center justify-center gap-2 py-6">
            <Spinner className="size-3.5" />
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Loading...</span>
          </ComboboxEmpty>
        ) : (
          <>
            <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
            <ComboboxList>
              {(label) => (
                <ComboboxItem key={label} value={label}>
                  {label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
}
